import React, { useState, useEffect } from "react";
import { useLocation, Routes, Route, Link, Navigate } from "react-router-dom";
import { Icon } from "./Icon";
import { buildResponseModePayload } from "./responseModePayload";

interface ResponseModeSettings {
  responseMode: "keyword" | "intelligent";
  customKeywords: string[];
  confidenceThreshold: number;
  eventResponderEnabled: boolean;
  dontRepeatModerators: boolean;
  moderatorDelay: number;
}

interface ResponseTriggerStats {
  keywordMatches: number;
  intelligentResponses: number;
  mentionResponses: number;
  replyResponses: number;
  totalResponses: number;
  timeRange: string;
  eventResponses: number;
}

interface ResponseModeControlProps {
  token: string;
  userType: "user" | "premium" | "vip" | "admin";
  botUsername?: string;
}

export const ResponseModeControl: React.FC<ResponseModeControlProps> = ({
  token,
  userType,
  botUsername = "hal_8580",
}) => {
  const location = useLocation();
  const [settings, setSettings] = useState<ResponseModeSettings>({
    responseMode: "keyword",
    customKeywords: ["hal"],
    confidenceThreshold: 70,
    eventResponderEnabled: true,
    dontRepeatModerators: false,
    moderatorDelay: 10,
  });
  const [originalSettings, setOriginalSettings] =
    useState<ResponseModeSettings>({
      responseMode: "keyword",
      customKeywords: ["hal"],
      confidenceThreshold: 70,
      eventResponderEnabled: true,
      dontRepeatModerators: false,
      moderatorDelay: 10,
    });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [keywordInput, setKeywordInput] = useState("");
  const [stats, setStats] = useState<ResponseTriggerStats>({
    keywordMatches: 0,
    intelligentResponses: 0,
    mentionResponses: 0,
    replyResponses: 0,
    totalResponses: 0,
    timeRange: "Last 24 hours",
    eventResponses: 0,
  });
  const [statsHours, setStatsHours] = useState(24);

  const isPremium =
    userType === "premium" || userType === "vip" || userType === "admin";

  // Check if settings have changed
  const hasChanges =
    JSON.stringify(settings) !== JSON.stringify(originalSettings);

  useEffect(() => {
    fetchSettings();
    fetchStats();
  }, []);

  // Force responseMode to keyword when viewing as free tier
  useEffect(() => {
    if (!isPremium && settings.responseMode === "intelligent") {
      setSettings((prev) => ({
        ...prev,
        responseMode: "keyword",
      }));
    }
  }, [isPremium, settings.responseMode]);

  useEffect(() => {
    fetchStats();
  }, [statsHours]);

  // Refresh stats every 10 seconds only when on Analytics tab
  useEffect(() => {
    // Only poll when on the analytics tab specifically
    if (!location.pathname.includes("/dashboard/control/responses/analytics")) {
      return;
    }

    const interval = setInterval(() => {
      fetchStats();
    }, 10000); // Poll every 10 seconds instead of 2

    return () => clearInterval(interval);
  }, [location.pathname, token, statsHours]);

  const fetchSettings = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch("/api/bot/response-mode", {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        // Force responseMode to keyword for free users
        if (!isPremium && data.responseMode === "intelligent") {
          data.responseMode = "keyword";
        }
        setSettings(data);
        setOriginalSettings(data);
      } else {
        setError("Failed to load response mode settings");
      }
    } catch (error) {
      console.error("Error fetching response mode settings:", error);
      setError("Error loading settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(
        `/api/bot/response-mode/stats?hours=${statsHours}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (response.ok) {
        const data = await response.json();
        setStats(data);
      } else {
        console.error("Failed to load response trigger stats");
      }
    } catch (error) {
      console.error("Error fetching response trigger stats:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await fetch("/api/bot/response-mode", {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(buildResponseModePayload(settings, isPremium)),
      });

      if (response.ok) {
        const result = await response.json();
        setSettings(result.settings);
        setOriginalSettings(result.settings);
        setSuccess("Response mode settings updated successfully!");
        setTimeout(() => setSuccess(null), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.message || "Failed to update settings");
      }
    } catch (error) {
      console.error("Error updating response mode:", error);
      setError("Error saving settings");
    } finally {
      setSaving(false);
    }
  };

  const addKeyword = () => {
    if (
      keywordInput.trim() &&
      !settings.customKeywords.includes(keywordInput.trim().toLowerCase())
    ) {
      setSettings({
        ...settings,
        customKeywords: [
          ...settings.customKeywords,
          keywordInput.trim().toLowerCase(),
        ],
      });
      setKeywordInput("");
    }
  };

  const removeKeyword = (keyword: string) => {
    setSettings({
      ...settings,
      customKeywords: settings.customKeywords.filter((k) => k !== keyword),
    });
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      addKeyword();
    }
  };

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: "#1a202c",
          padding: "20px",
          borderRadius: "8px",
          border: "1px solid #4a5568",
          marginBottom: "20px",
        }}
      >
        <div style={{ color: "#e2e8f0", textAlign: "center" }}>
          Loading response mode settings...
        </div>
      </div>
    );
  }

  // Get active tab from URL
  const pathSegments = location.pathname.split("/");
  const activeTab = pathSegments[4] || "keyword";

  return (
    <div>
      <h3
        style={{
          color: "#e2e8f0",
          marginTop: 0,
          marginBottom: "20px",
          fontSize: "18px",
        }}
      >
        <Icon
          name="target"
          size={18}
          style={{ verticalAlign: "-3px", marginRight: "8px" }}
        />
        Response Trigger Settings
      </h3>

      {/* Tab Navigation */}
      <div
        style={{
          display: "flex",
          gap: "20px",
          marginBottom: "24px",
          borderBottom: "1px solid #4a5568",
          paddingBottom: "12px",
        }}
      >
        <Link
          to="/dashboard/control/responses/keyword"
          style={{
            color: activeTab === "keyword" ? "#9146FF" : "#cbd5e0",
            textDecoration: "none",
            fontSize: "16px",
            fontWeight: activeTab === "keyword" ? "600" : "400",
            paddingBottom: "12px",
            borderBottom:
              activeTab === "keyword"
                ? "2px solid #9146FF"
                : "2px solid transparent",
            transition: "all 0.2s",
          }}
        >
          Keyword Detection
        </Link>
        <Link
          to="/dashboard/control/responses/intelligent"
          style={{
            color: activeTab === "intelligent" ? "#9146FF" : "#cbd5e0",
            textDecoration: "none",
            fontSize: "16px",
            fontWeight: activeTab === "intelligent" ? "600" : "400",
            paddingBottom: "12px",
            borderBottom:
              activeTab === "intelligent"
                ? "2px solid #9146FF"
                : "2px solid transparent",
            transition: "all 0.2s",
          }}
        >
          Intelligent Response Detection
        </Link>
        <Link
          to="/dashboard/control/responses/event"
          style={{
            color: activeTab === "event" ? "#9146FF" : "#cbd5e0",
            textDecoration: "none",
            fontSize: "16px",
            fontWeight: activeTab === "event" ? "600" : "400",
            paddingBottom: "12px",
            borderBottom:
              activeTab === "event"
                ? "2px solid #9146FF"
                : "2px solid transparent",
            transition: "all 0.2s",
          }}
        >
          Event Responder
        </Link>
        <Link
          to="/dashboard/control/responses/analytics"
          style={{
            color: activeTab === "analytics" ? "#9146FF" : "#cbd5e0",
            textDecoration: "none",
            fontSize: "16px",
            fontWeight: activeTab === "analytics" ? "600" : "400",
            paddingBottom: "12px",
            borderBottom:
              activeTab === "analytics"
                ? "2px solid #9146FF"
                : "2px solid transparent",
            transition: "all 0.2s",
          }}
        >
          Analytics
        </Link>
      </div>

      {error && (
        <div
          style={{
            color: "#fed7d7",
            backgroundColor: "rgba(245, 101, 101, 0.1)",
            border: "1px solid rgba(245, 101, 101, 0.3)",
            padding: "10px",
            borderRadius: "4px",
            marginBottom: "15px",
            fontSize: "14px",
          }}
        >
          {error}
        </div>
      )}

      {success && (
        <div
          style={{
            color: "#c6f6d5",
            backgroundColor: "rgba(72, 187, 120, 0.1)",
            border: "1px solid rgba(72, 187, 120, 0.3)",
            padding: "10px",
            borderRadius: "4px",
            marginBottom: "15px",
            fontSize: "14px",
          }}
        >
          {success}
        </div>
      )}

      {/* Routes for tabs */}
      <Routes>
        <Route path="/" element={<Navigate to="keyword" replace />} />
        <Route
          path="keyword"
          element={
            <div
              style={{
                backgroundColor: "#1a202c",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #4a5568",
              }}
            >
              {/* Always Active: Keywords */}
              <div style={{ marginBottom: "20px" }}>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    marginBottom: "8px",
                  }}
                >
                  <label
                    style={{
                      color: "#e2e8f0",
                      fontSize: "14px",
                      fontWeight: "500",
                    }}
                  >
                    <Icon
                      name="type"
                      size={16}
                      style={{ verticalAlign: "-3px", marginRight: "8px" }}
                    />
                    Keyword Detection
                  </label>
                  <span
                    style={{
                      backgroundColor: "#48bb78",
                      color: "white",
                      fontSize: "10px",
                      padding: "2px 6px",
                      borderRadius: "4px",
                      fontWeight: "600",
                    }}
                  >
                    ALWAYS ON
                  </span>
                </div>
                <p
                  style={{
                    color: "#a0aec0",
                    fontSize: "12px",
                    margin: "0 0 12px 0",
                    lineHeight: "1.4",
                  }}
                >
                  Bot always checks for your keywords first. Configure them
                  below.
                </p>
              </div>

              {/* Keyword Configuration */}
              <div style={{ marginBottom: "20px" }}>
                <label
                  style={{
                    color: "#e2e8f0",
                    fontSize: "14px",
                    fontWeight: "500",
                    display: "block",
                    marginBottom: "8px",
                  }}
                >
                  Trigger Keywords
                </label>
                <div
                  style={{ display: "flex", gap: "8px", marginBottom: "10px" }}
                >
                  <input
                    type="text"
                    value={keywordInput}
                    onChange={(e) => setKeywordInput(e.target.value)}
                    onKeyPress={handleKeyPress}
                    placeholder="Enter a keyword..."
                    style={{
                      flex: 1,
                      backgroundColor: "#2d3748",
                      border: "1px solid #4a5568",
                      borderRadius: "4px",
                      padding: "8px 12px",
                      color: "#e2e8f0",
                      fontSize: "14px",
                    }}
                  />
                  <button
                    onClick={addKeyword}
                    disabled={!keywordInput.trim()}
                    style={{
                      backgroundColor: "#9146FF",
                      color: "white",
                      border: "none",
                      borderRadius: "4px",
                      padding: "8px 16px",
                      cursor: keywordInput.trim() ? "pointer" : "not-allowed",
                      fontSize: "14px",
                      opacity: keywordInput.trim() ? 1 : 0.5,
                    }}
                  >
                    Add
                  </button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {settings.customKeywords.map((keyword) => (
                    <span
                      key={keyword}
                      style={{
                        backgroundColor: "rgba(145, 70, 255, 0.2)",
                        color: "#ddd6fe",
                        padding: "4px 8px",
                        borderRadius: "4px",
                        fontSize: "12px",
                        display: "flex",
                        alignItems: "center",
                        gap: "4px",
                        border: "1px solid #9146FF",
                      }}
                    >
                      {keyword}
                      <button
                        onClick={() => removeKeyword(keyword)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#ddd6fe",
                          cursor: "pointer",
                          fontSize: "14px",
                          padding: "0 2px",
                        }}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
                <p
                  style={{
                    color: "#a0aec0",
                    fontSize: "12px",
                    margin: "8px 0 0 0",
                    lineHeight: "1.4",
                  }}
                >
                  Bot will respond when any of these keywords are found in
                  messages. Default: &quot;hal&quot;
                </p>
              </div>

              {/* Save Button */}
              <button
                onClick={handleSave}
                disabled={saving || !hasChanges}
                style={{
                  backgroundColor:
                    saving || !hasChanges ? "#4a5568" : "#9146FF",
                  color: "white",
                  border: "none",
                  padding: "10px 20px",
                  borderRadius: "6px",
                  cursor: saving || !hasChanges ? "not-allowed" : "pointer",
                  fontSize: "14px",
                  fontWeight: "500",
                  opacity: saving || !hasChanges ? 0.7 : 1,
                }}
              >
                {saving ? "Saving..." : "Save Settings"}
              </button>
            </div>
          }
        />
        <Route
          path="intelligent"
          element={
            <div
              style={{
                backgroundColor: "#1a202c",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #4a5568",
              }}
            >
              {!isPremium ? (
                // Premium feature block for free users
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{ marginBottom: "20px" }}>
                    <Icon name="brain" size={48} />
                  </div>
                  <h4
                    style={{
                      color: "#e2e8f0",
                      fontSize: "20px",
                      marginBottom: "16px",
                    }}
                  >
                    Intelligent Response Detection - Premium Feature
                  </h4>
                  <p
                    style={{
                      color: "#a0aec0",
                      fontSize: "14px",
                      marginBottom: "24px",
                      maxWidth: "600px",
                      margin: "0 auto 24px",
                      lineHeight: "1.6",
                    }}
                  >
                    Take HAL to the next level with AI-powered context analysis!
                    HAL will intelligently understand when users are talking to
                    him, even without keywords - analyzing conversation context,
                    user intent, and chat flow to provide natural, timely
                    responses.
                  </p>
                  <div
                    style={{
                      backgroundColor: "rgba(145, 70, 255, 0.1)",
                      border: "1px solid #9146FF",
                      borderRadius: "8px",
                      padding: "20px",
                      marginBottom: "24px",
                      maxWidth: "500px",
                      margin: "0 auto 24px",
                    }}
                  >
                    <h5
                      style={{
                        color: "#9146FF",
                        marginTop: 0,
                        marginBottom: "12px",
                        fontSize: "16px",
                      }}
                    >
                      <Icon
                        name="sparkles"
                        size={16}
                        style={{ verticalAlign: "-3px", marginRight: "8px" }}
                      />
                      Premium Benefits:
                    </h5>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        textAlign: "left",
                        margin: 0,
                        paddingLeft: "20px",
                        fontSize: "13px",
                        lineHeight: "1.8",
                      }}
                    >
                      <li>AI-powered context understanding</li>
                      <li>Natural conversation flow detection</li>
                      <li>Adjustable confidence threshold</li>
                      <li>Works alongside keyword detection</li>
                    </ul>
                  </div>
                  <button
                    onClick={() =>
                      (window.location.href = "/dashboard/account")
                    }
                    style={{
                      backgroundColor: "#9146FF",
                      color: "white",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 15px rgba(145, 70, 255, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <Icon
                      name="rocket"
                      size={16}
                      style={{ verticalAlign: "-3px", marginRight: "8px" }}
                    />
                    Upgrade to Premium
                  </button>
                </div>
              ) : (
                <>
                  {/* Intelligent Response Detection */}
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          color: "#e2e8f0",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <Icon
                          name="brain"
                          size={16}
                          style={{ verticalAlign: "-3px", marginRight: "8px" }}
                        />
                        Intelligent Response Detection
                      </label>
                      {!isPremium && (
                        <span
                          style={{
                            backgroundColor: "#e53e3e",
                            color: "white",
                            fontSize: "10px",
                            padding: "2px 6px",
                            borderRadius: "4px",
                            fontWeight: "600",
                          }}
                        >
                          PREMIUM ONLY
                        </span>
                      )}
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <button
                        onClick={() => {
                          if (isPremium) {
                            setSettings({
                              ...settings,
                              responseMode:
                                settings.responseMode === "intelligent"
                                  ? "keyword"
                                  : "intelligent",
                            });
                          }
                        }}
                        disabled={!isPremium}
                        style={{
                          backgroundColor:
                            settings.responseMode === "intelligent"
                              ? "#9146FF"
                              : "rgba(145, 70, 255, 0.2)",
                          color:
                            settings.responseMode === "intelligent"
                              ? "white"
                              : isPremium
                                ? "#9146FF"
                                : "#718096",
                          border: `1px solid ${isPremium ? "#9146FF" : "#4a5568"}`,
                          padding: "8px 16px",
                          borderRadius: "6px",
                          cursor: isPremium ? "pointer" : "not-allowed",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        {settings.responseMode === "intelligent" ? (
                          <>
                            <Icon
                              name="check"
                              size={14}
                              color="#10B981"
                              style={{
                                verticalAlign: "-2px",
                                marginRight: "4px",
                              }}
                            />
                            Enabled
                          </>
                        ) : (
                          <>
                            <Icon
                              name="circle"
                              size={14}
                              style={{
                                verticalAlign: "-2px",
                                marginRight: "4px",
                              }}
                            />
                            Disabled
                          </>
                        )}
                      </button>
                    </div>
                    <p
                      style={{
                        color: "#a0aec0",
                        fontSize: "12px",
                        margin: "0",
                        lineHeight: "1.4",
                      }}
                    >
                      When enabled, if no keywords match, intelligently analyzes
                      context to determine if the message is intended for the
                      bot.
                      {!isPremium &&
                        " Upgrade to Premium to enable this feature."}
                    </p>
                  </div>

                  {/* Confidence Threshold for Intelligent Mode */}
                  {settings.responseMode === "intelligent" && isPremium && (
                    <div style={{ marginBottom: "20px" }}>
                      <label
                        style={{
                          color: "#e2e8f0",
                          fontSize: "14px",
                          fontWeight: "500",
                          display: "block",
                          marginBottom: "8px",
                        }}
                      >
                        Response Confidence Threshold:{" "}
                        {settings.confidenceThreshold}%
                      </label>
                      <input
                        type="range"
                        min="0"
                        max="100"
                        value={settings.confidenceThreshold}
                        onChange={(e) =>
                          setSettings({
                            ...settings,
                            confidenceThreshold: parseInt(e.target.value),
                          })
                        }
                        style={{
                          width: "100%",
                          marginBottom: "8px",
                        }}
                      />
                      <div
                        style={{
                          display: "flex",
                          justifyContent: "space-between",
                          color: "#a0aec0",
                          fontSize: "12px",
                        }}
                      >
                        <span>More Responses (0%)</span>
                        <span>Fewer Responses (100%)</span>
                      </div>
                      <p
                        style={{
                          color: "#a0aec0",
                          fontSize: "12px",
                          margin: "8px 0 0 0",
                          lineHeight: "1.4",
                        }}
                      >
                        Higher values mean the bot will only respond when very
                        confident the message is intended for it. Lower values
                        mean more responses but potentially to unintended
                        messages.
                      </p>
                    </div>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    style={{
                      backgroundColor:
                        saving || !hasChanges ? "#4a5568" : "#9146FF",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: saving || !hasChanges ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      opacity: saving || !hasChanges ? 0.7 : 1,
                    }}
                  >
                    {saving ? "Saving..." : "Save Settings"}
                  </button>
                </>
              )}
            </div>
          }
        />
        <Route
          path="event"
          element={
            <div
              style={{
                backgroundColor: "#1a202c",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #4a5568",
              }}
            >
              {!isPremium ? (
                // Premium feature block for free users
                <div style={{ textAlign: "center", padding: "40px 20px" }}>
                  <div style={{ marginBottom: "20px" }}>
                    <Icon name="party-popper" size={48} />
                  </div>
                  <h4
                    style={{
                      color: "#e2e8f0",
                      fontSize: "20px",
                      marginBottom: "16px",
                    }}
                  >
                    Event Responder - Premium Feature
                  </h4>
                  <p
                    style={{
                      color: "#a0aec0",
                      fontSize: "14px",
                      marginBottom: "24px",
                      maxWidth: "600px",
                      margin: "0 auto 24px",
                      lineHeight: "1.6",
                    }}
                  >
                    HAL can automatically respond to various Twitch events like
                    raids, subscriptions, follows, and more! Configure
                    intelligent responses to welcome new followers, thank
                    raiders, and celebrate subscribers - all while respecting
                    your moderators&apos; responses.
                  </p>
                  <div
                    style={{
                      backgroundColor: "rgba(145, 70, 255, 0.1)",
                      border: "1px solid #9146FF",
                      borderRadius: "8px",
                      padding: "20px",
                      marginBottom: "24px",
                      maxWidth: "500px",
                      margin: "0 auto 24px",
                    }}
                  >
                    <h5
                      style={{
                        color: "#9146FF",
                        marginTop: 0,
                        marginBottom: "12px",
                        fontSize: "16px",
                      }}
                    >
                      <Icon
                        name="sparkles"
                        size={16}
                        style={{ verticalAlign: "-3px", marginRight: "8px" }}
                      />
                      Premium Benefits:
                    </h5>
                    <ul
                      style={{
                        color: "#cbd5e0",
                        textAlign: "left",
                        margin: 0,
                        paddingLeft: "20px",
                        fontSize: "13px",
                        lineHeight: "1.8",
                      }}
                    >
                      <li>
                        Automatic responses to raids, subs, follows, and more
                      </li>
                      <li>
                        Smart moderator detection - won&apos;t duplicate their
                        welcomes
                      </li>
                      <li>
                        Customizable delay to give mods time to respond first
                      </li>
                      <li>High priority message queue for timely responses</li>
                    </ul>
                  </div>
                  <button
                    onClick={() =>
                      (window.location.href = "/dashboard/account")
                    }
                    style={{
                      backgroundColor: "#9146FF",
                      color: "white",
                      border: "none",
                      padding: "12px 24px",
                      borderRadius: "8px",
                      fontSize: "16px",
                      fontWeight: "bold",
                      cursor: "pointer",
                      transition: "all 0.2s",
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-2px)";
                      e.currentTarget.style.boxShadow =
                        "0 4px 15px rgba(145, 70, 255, 0.4)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "none";
                    }}
                  >
                    <Icon
                      name="rocket"
                      size={16}
                      style={{ verticalAlign: "-3px", marginRight: "8px" }}
                    />
                    Upgrade to Premium
                  </button>
                </div>
              ) : (
                // Event Responder settings for premium users
                <>
                  {/* Event Responder Toggle */}
                  <div style={{ marginBottom: "20px" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <label
                        style={{
                          color: "#e2e8f0",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        <Icon
                          name="party-popper"
                          size={16}
                          style={{ verticalAlign: "-3px", marginRight: "8px" }}
                        />
                        Event Responder
                      </label>
                    </div>
                    <div style={{ marginBottom: "10px" }}>
                      <button
                        onClick={() => {
                          setSettings({
                            ...settings,
                            eventResponderEnabled:
                              !settings.eventResponderEnabled,
                          });
                        }}
                        style={{
                          backgroundColor: settings.eventResponderEnabled
                            ? "#9146FF"
                            : "rgba(145, 70, 255, 0.2)",
                          color: settings.eventResponderEnabled
                            ? "white"
                            : "#9146FF",
                          border: "1px solid #9146FF",
                          padding: "8px 16px",
                          borderRadius: "6px",
                          cursor: "pointer",
                          fontSize: "14px",
                          fontWeight: "500",
                        }}
                      >
                        {settings.eventResponderEnabled ? (
                          <>
                            <Icon
                              name="check"
                              size={14}
                              color="#10B981"
                              style={{
                                verticalAlign: "-2px",
                                marginRight: "4px",
                              }}
                            />
                            Enabled
                          </>
                        ) : (
                          <>
                            <Icon
                              name="circle"
                              size={14}
                              style={{
                                verticalAlign: "-2px",
                                marginRight: "4px",
                              }}
                            />
                            Disabled
                          </>
                        )}
                      </button>
                    </div>
                    <p
                      style={{
                        color: "#a0aec0",
                        fontSize: "12px",
                        margin: "0",
                        lineHeight: "1.4",
                      }}
                    >
                      Automatically respond to Twitch events like raids,
                      subscriptions, follows, and more.
                    </p>
                  </div>

                  {/* Don't Repeat Moderators */}
                  {settings.eventResponderEnabled && (
                    <div style={{ marginBottom: "20px" }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          marginBottom: "8px",
                        }}
                      >
                        <label
                          style={{
                            color: "#e2e8f0",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          <Icon
                            name="shield-check"
                            size={16}
                            style={{
                              verticalAlign: "-3px",
                              marginRight: "8px",
                            }}
                          />
                          Don&apos;t Repeat Moderators
                        </label>
                      </div>
                      <div style={{ marginBottom: "10px" }}>
                        <button
                          onClick={() => {
                            setSettings({
                              ...settings,
                              dontRepeatModerators:
                                !settings.dontRepeatModerators,
                            });
                          }}
                          style={{
                            backgroundColor: settings.dontRepeatModerators
                              ? "#48bb78"
                              : "rgba(72, 187, 120, 0.2)",
                            color: settings.dontRepeatModerators
                              ? "white"
                              : "#48bb78",
                            border: "1px solid #48bb78",
                            padding: "8px 16px",
                            borderRadius: "6px",
                            cursor: "pointer",
                            fontSize: "14px",
                            fontWeight: "500",
                          }}
                        >
                          {settings.dontRepeatModerators ? (
                            <>
                              <Icon
                                name="check"
                                size={14}
                                color="#10B981"
                                style={{
                                  verticalAlign: "-2px",
                                  marginRight: "4px",
                                }}
                              />
                              Enabled
                            </>
                          ) : (
                            <>
                              <Icon
                                name="circle"
                                size={14}
                                style={{
                                  verticalAlign: "-2px",
                                  marginRight: "4px",
                                }}
                              />
                              Disabled
                            </>
                          )}
                        </button>
                      </div>
                      <p
                        style={{
                          color: "#a0aec0",
                          fontSize: "12px",
                          margin: "0 0 12px 0",
                          lineHeight: "1.4",
                        }}
                      >
                        When enabled, HAL will wait for moderators to respond to
                        events first. If no moderator acknowledges the event
                        within the delay period, HAL will respond.
                      </p>

                      {/* Moderator Delay Slider */}
                      {settings.dontRepeatModerators && (
                        <div>
                          <label
                            style={{
                              color: "#e2e8f0",
                              fontSize: "14px",
                              fontWeight: "500",
                              display: "block",
                              marginBottom: "8px",
                            }}
                          >
                            Moderator Response Delay: {settings.moderatorDelay}{" "}
                            seconds
                          </label>
                          <input
                            type="range"
                            min="3"
                            max="30"
                            value={settings.moderatorDelay}
                            onChange={(e) =>
                              setSettings({
                                ...settings,
                                moderatorDelay: parseInt(e.target.value),
                              })
                            }
                            style={{
                              width: "100%",
                              marginBottom: "8px",
                            }}
                          />
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              color: "#a0aec0",
                              fontSize: "12px",
                            }}
                          >
                            <span>Quick (3s)</span>
                            <span>Patient (30s)</span>
                          </div>
                          <p
                            style={{
                              color: "#a0aec0",
                              fontSize: "12px",
                              margin: "8px 0 0 0",
                              lineHeight: "1.4",
                            }}
                          >
                            HAL will wait this long for a moderator to respond
                            before sending his own message.
                          </p>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Event Types Info */}
                  {settings.eventResponderEnabled && (
                    <div
                      style={{
                        backgroundColor: "#2d3748",
                        border: "1px solid #4a5568",
                        borderRadius: "6px",
                        padding: "16px",
                        marginBottom: "20px",
                      }}
                    >
                      <h5
                        style={{
                          color: "#e2e8f0",
                          marginTop: 0,
                          marginBottom: "12px",
                          fontSize: "14px",
                        }}
                      >
                        <Icon
                          name="clipboard-list"
                          size={16}
                          style={{ verticalAlign: "-3px", marginRight: "8px" }}
                        />
                        Supported Events
                      </h5>
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fit, minmax(200px, 1fr))",
                          gap: "8px",
                          fontSize: "12px",
                          color: "#cbd5e0",
                        }}
                      >
                        <div>
                          •{" "}
                          <Icon
                            name="target"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Raids
                        </div>
                        <div>
                          •{" "}
                          <Icon
                            name="heart"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Subscriptions
                        </div>
                        <div>
                          •{" "}
                          <Icon
                            name="gift"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Gift Subs
                        </div>
                        <div>
                          •{" "}
                          <Icon
                            name="heart"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Follows
                        </div>
                        <div>
                          •{" "}
                          <Icon
                            name="gem"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Bits/Cheers
                        </div>
                        <div>
                          •{" "}
                          <Icon
                            name="party-popper"
                            size={14}
                            style={{
                              verticalAlign: "-2px",
                              marginRight: "4px",
                            }}
                          />
                          Re-subscriptions
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Save Button */}
                  <button
                    onClick={handleSave}
                    disabled={saving || !hasChanges}
                    style={{
                      backgroundColor:
                        saving || !hasChanges ? "#4a5568" : "#9146FF",
                      color: "white",
                      border: "none",
                      padding: "10px 20px",
                      borderRadius: "6px",
                      cursor: saving || !hasChanges ? "not-allowed" : "pointer",
                      fontSize: "14px",
                      fontWeight: "500",
                      opacity: saving || !hasChanges ? 0.7 : 1,
                    }}
                  >
                    {saving ? "Saving..." : "Save Settings"}
                  </button>
                </>
              )}
            </div>
          }
        />
        <Route
          path="analytics"
          element={
            <div
              style={{
                backgroundColor: "#1a202c",
                padding: "20px",
                borderRadius: "8px",
                border: "1px solid #4a5568",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  marginBottom: "20px",
                }}
              >
                <h4
                  style={{
                    color: "#e2e8f0",
                    fontSize: "18px",
                    fontWeight: "500",
                    margin: 0,
                  }}
                >
                  <Icon
                    name="bar-chart-3"
                    size={18}
                    style={{ verticalAlign: "-3px", marginRight: "8px" }}
                  />
                  Response Statistics
                </h4>
                <div style={{ position: "relative" }}>
                  <select
                    value={statsHours}
                    onChange={(e) => setStatsHours(parseInt(e.target.value))}
                    style={{
                      padding: "10px 40px 10px 10px",
                      backgroundColor: "#2d3748",
                      color: "#e2e8f0",
                      border: "1px solid #4a5568",
                      borderRadius: "4px",
                      fontSize: "14px",
                      outline: "none",
                      appearance: "none",
                      width: "100%",
                    }}
                  >
                    <option value={1}>Last hour</option>
                    <option value={24}>Last 24 hours</option>
                    <option value={168}>Last 7 days</option>
                  </select>
                  <div
                    style={{
                      position: "absolute",
                      right: "15px",
                      top: "50%",
                      transform: "translateY(-50%)",
                      pointerEvents: "none",
                      color: "#cbd5e0",
                      fontSize: "14px",
                    }}
                  >
                    ▼
                  </div>
                </div>
              </div>

              <div
                style={{
                  backgroundColor: "#2d3748",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  padding: "20px",
                }}
              >
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(3, 1fr)",
                    gap: "16px",
                    textAlign: "center",
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#9146FF",
                        marginBottom: "6px",
                      }}
                    >
                      {stats.keywordMatches}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      <Icon
                        name="type"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Keywords
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#48bb78",
                        marginBottom: "6px",
                      }}
                    >
                      {stats.intelligentResponses}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      <Icon
                        name="brain"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Intelligent
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#f6ad55",
                        marginBottom: "6px",
                      }}
                    >
                      {stats.mentionResponses}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      <Icon
                        name="megaphone"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Mentions
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#63b3ed",
                        marginBottom: "6px",
                      }}
                    >
                      {stats.replyResponses}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      <Icon
                        name="message-square"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Replies
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#f687b3",
                        marginBottom: "6px",
                      }}
                    >
                      {stats.eventResponses || 0}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      <Icon
                        name="party-popper"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Events
                    </div>
                  </div>

                  <div>
                    <div
                      style={{
                        fontSize: "24px",
                        fontWeight: "bold",
                        color: "#e2e8f0",
                        marginBottom: "6px",
                      }}
                    >
                      {stats.totalResponses}
                    </div>
                    <div
                      style={{
                        fontSize: "12px",
                        color: "#a0aec0",
                        fontWeight: "500",
                      }}
                    >
                      <Icon
                        name="trending-up"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Total
                    </div>
                  </div>
                </div>
              </div>

              <div
                style={{
                  marginTop: "20px",
                  backgroundColor: "#2d3748",
                  border: "1px solid #4a5568",
                  borderRadius: "6px",
                  padding: "16px",
                }}
              >
                <h5
                  style={{
                    color: "#e2e8f0",
                    marginTop: 0,
                    marginBottom: "12px",
                    fontSize: "14px",
                  }}
                >
                  <Icon
                    name="lightbulb"
                    size={16}
                    style={{ verticalAlign: "-3px", marginRight: "8px" }}
                  />
                  Understanding Response Types
                </h5>
                <div
                  style={{
                    fontSize: "12px",
                    color: "#a0aec0",
                    lineHeight: "1.6",
                  }}
                >
                  <div style={{ marginBottom: "8px" }}>
                    <strong style={{ color: "#cbd5e0" }}>
                      <Icon
                        name="type"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Keywords:
                    </strong>{" "}
                    Bot responded to configured trigger keywords
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong style={{ color: "#cbd5e0" }}>
                      <Icon
                        name="brain"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Intelligent:
                    </strong>{" "}
                    AI detected the message was intended for the bot
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong style={{ color: "#cbd5e0" }}>
                      <Icon
                        name="megaphone"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Mentions:
                    </strong>{" "}
                    Direct @mentions of the bot
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong style={{ color: "#cbd5e0" }}>
                      <Icon
                        name="message-square"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Replies:
                    </strong>{" "}
                    Responses to bot&apos;s previous messages
                  </div>
                  <div style={{ marginBottom: "8px" }}>
                    <strong style={{ color: "#cbd5e0" }}>
                      <Icon
                        name="party-popper"
                        size={14}
                        style={{ verticalAlign: "-2px", marginRight: "4px" }}
                      />
                      Events:
                    </strong>{" "}
                    Automatic responses to Twitch events (raids, subs, follows)
                  </div>
                </div>
              </div>
            </div>
          }
        />
      </Routes>
    </div>
  );
};
